import { useEffect, useRef } from "react";
import { Call } from "@twilio/voice-sdk";
import { useCalling } from "../hooks/calling";
import { CallWindow } from "../components/CallWindow";
import { showBrowserCallNotification } from "../notifications/CallNotification";

export interface CallingProviderProps {
  children: React.ReactNode;
}

/**
 * CallingProvider - Initializes calling functionality and renders the CallWindow.
 *
 * This provider should wrap authenticated content (inside ProtectedRoute) and provides:
 * - Automatic Twilio device initialization on mount via TanStack Query mutation
 * - Browser notifications for incoming calls (when tab is in background)
 * - The CallWindow component for managing calls
 *
 * @example
 * ```tsx
 * // In ProtectedRoute.tsx
 * export const ProtectedRoute = ({ children }) => {
 *   // ... auth checks ...
 *   return <CallingProvider>{children}</CallingProvider>;
 * };
 * ```
 */
export function CallingProvider({ children }: CallingProviderProps) {
  const { initializeDevice, destroyDevice, isInitialized, isInitializing, incomingCalls } = useCalling();
  const browserNotificationsRef = useRef<Map<string, Notification>>(new Map());
  const previousIncomingCallsRef = useRef<Call[]>([]);
  const initializationAttemptedRef = useRef(false);

  // Initialize device on mount using TanStack Query mutation
  // This avoids useEffect dependency issues while still handling initialization properly
  if (!initializationAttemptedRef.current && !isInitialized && !isInitializing) {
    initializationAttemptedRef.current = true;
    initializeDevice().then((device) => {
      if (device) {
        console.log("[Calling:Provider] Twilio device initialized");
      } else {
        console.log("[Calling:Provider] Twilio device not initialized (no permissions or config)");
      }
    });
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      destroyDevice();
    };
  }, [destroyDevice]);

  // Show browser notifications for incoming calls (when tab is in background)
  useEffect(() => {
    const currentCalls = incomingCalls;
    const previousCalls = previousIncomingCallsRef.current;

    // Find new incoming calls
    const newCalls = currentCalls.filter(
      (call) => !previousCalls.some((prev) => prev.parameters?.CallSid === call.parameters?.CallSid)
    );

    // Find removed calls (answered, rejected, or cancelled)
    const removedCalls = previousCalls.filter(
      (call) => !currentCalls.some((curr) => curr.parameters?.CallSid === call.parameters?.CallSid)
    );

    // Show browser notifications for new calls
    newCalls.forEach(async (call) => {
      const callSid = call.parameters?.CallSid;
      if (!callSid) return;

      // Show browser notification if page is hidden
      const callerName = call.customParameters?.get("eName") ?? call.parameters?.From ?? "Unknown";
      const browserNotification = await showBrowserCallNotification(callerName);
      if (browserNotification) {
        browserNotificationsRef.current.set(callSid, browserNotification);

        // Handle click on browser notification - focus window
        browserNotification.onclick = () => {
          window.focus();
          browserNotification.close();
        };
      }

      console.log("[Calling:Provider] Incoming call:", callSid);
    });

    // Close browser notifications for removed calls
    removedCalls.forEach((call) => {
      const callSid = call.parameters?.CallSid;
      if (!callSid) return;

      // Close browser notification
      const browserNotification = browserNotificationsRef.current.get(callSid);
      if (browserNotification) {
        browserNotification.close();
        browserNotificationsRef.current.delete(callSid);
      }

      console.log("[Calling:Provider] Incoming call ended:", callSid);
    });

    // Update previous calls ref
    previousIncomingCallsRef.current = currentCalls;
  }, [incomingCalls]);

  return (
    <>
      {children}
      <CallWindow />
    </>
  );
}
